import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Observer } from 'gsap/Observer';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(Observer);

const selectors = {
    el: '.js-sprite:not(.is-ready)',
    container: '.js-sprite__container',
    labelsContainer: '.js-sprite__labels-container',
    label: '.js-sprite__label',
    text: '.js-sprite__text'
};

const classes = {
    ready: 'is-ready',
    active: 'is-active',
    gone: 'is-gone'
}

/**
 * @function `getIntersection`
 * Determine if a sprite intersects any other placed sprites. If no,
 * returns undefined; if yes, returns the intersecting sprite
 * for comparison
 *
 * @param {object} sprite - The sprite to compare against others
 * @param {array} obstacles - An array of sprites already placed
 * @return undefined|object
 */
const getIntersection = (sprite, obstacles) => {
    let x1, x2, y1, y2;
    const intersections = [];
    let intersection;

    obstacles.forEach(obstacle => {
        x1 = (obstacle.x < (sprite.x + sprite.width));
        y1 = (obstacle.y < (sprite.y + sprite.height));
        x2 = ((obstacle.x + obstacle.width) > sprite.x);
        y2 = ((obstacle.y + obstacle.height) > sprite.y);

        if (x1 && x2 && y1 && y2) {
            intersections.push(obstacle);
        }
    });

    if (intersections.length) {
        intersection = intersections.pop();
    }

    return intersection;
}

/**
 * @function `placeSprite`
 * Determine sprite coordinates on the canvas. Once a position is
 * determined with no intersections, the sprite is added to the
 * placed array. If there is no space, the dimensions are updated.
 * Seeks across, then down, to place the sprite.
 *
 * @param {object} sprite - The sprite to place
 * @param {array} placed - An array of sprites already placed
 * @param {object} dimensions - The current canvas dimensions
 */
const placeSprite = (sprite, placed, dimensions) => {
    let intersection;
    let pass = 0;
    let x = 0;
    let y = 0;

    const findFreeSpace = () => {
        for (y = 0; y <= (dimensions.height - sprite.height); y++) {
            for (x = 0; x <= (dimensions.width - sprite.width); x++) {
                sprite.x = x;
                sprite.y = y;

                intersection = getIntersection(sprite, placed);

                if (!intersection) {
                    placed.push(sprite);
                    return true;
                }

                x = intersection.x + intersection.width - 1;
            }

            y = intersection.y + intersection.height - 1;
        }

        dimensions.width += sprite.width;
        dimensions.height += sprite.height;
        pass++;

        if (pass < 2) {
            requestAnimationFrame(findFreeSpace);
        }
    };

    requestAnimationFrame(findFreeSpace);
};


const build = el => {
    el.classList.add(classes.ready);
    const container = el.querySelector(selectors.container);
    const textBlocks = el.querySelectorAll(selectors.text);
    const labelsContainer = el.querySelector(selectors.labelsContainer);
    let labels;
    if (labelsContainer) {
        labels = [...labelsContainer.querySelectorAll(selectors.label)];
    }

    const dimensions = {
        width: container.getBoundingClientRect().width * el.dataset.xImages,
        height: container.getBoundingClientRect().height * el.dataset.yImages
    };

    const placed = [];

    for (let i = 0; i < el.dataset.totalImages; i++) {
        const sprite = {
            width: container.getBoundingClientRect().width,
            height: container.getBoundingClientRect().height,
            x: 0,
            y: 0
        };
        placeSprite(sprite, placed, dimensions);
    }

    const fraction = 1 / el.dataset.totalImages;
    let textFraction;
    if (textBlocks) {
        textFraction = 1 / (textBlocks.length + 2);
    }
    let prevIdx = 0;
    let prevTextIdx = 0;

    el.animation = gsap.from(el, {
        scrollTrigger: {
            trigger: el,
            start: 'top top',
            end: `bottom ${window.innerHeight + 1}px`,
            markers: false,
            onEnter: () => el.classList.add(classes.active),
            onLeaveBack: () => {
                el.classList.remove(classes.active);
                textBlocks.forEach(text => {
                    text.classList.remove(classes.active);
                });
            },
            onUpdate: self => {
                const idx = Math.floor(self.progress / fraction);
                if (idx === prevIdx) return;
                if (placed[idx]) {
                    container.style.backgroundPosition = `-${placed[idx].x}px -${placed[idx].y}px`;
                    if (labelsContainer) {
                        labelsContainer.style.top = `-${placed[idx].y}px`;
                        labelsContainer.style.left = `-${placed[idx].x}px`;
                    }
                }
                prevIdx = idx;

                // Text blocks
                if (textBlocks.length) {
                    const textIdx = Math.floor(self.progress / textFraction);
                    if (prevTextIdx === textIdx) return;
                    textBlocks.forEach((text, i) => {
                        text.classList.remove(classes.active);
                        if (i === textIdx - 2) {
                            text.classList.add(classes.gone);
                        } else {
                            text.classList.remove(classes.gone);
                        }
                    });
                    if (textBlocks[textIdx - 1]) {
                        document.body.classList.add('body--no-scroll-y');
                        setTimeout(() => document.body.classList.remove('body--no-scroll-y'), 2000);
                        textBlocks[textIdx - 1].classList.add(classes.active);
                    }
                    prevTextIdx = textIdx;
                }

                // Labels
                if (!labels) return;  
                const labelsInViewport = labels.filter(label => {
                    const containerRect = container.getBoundingClientRect();
                    const labelRect = label.getBoundingClientRect();
                    return labelRect.left >= containerRect.left &&
                           labelRect.top >= containerRect.top &&
                           labelRect.right <= containerRect.right &&
                           labelRect.bottom <= containerRect.bottom;
                });
                if (labelsInViewport.length) {
                    document.body.classList.add('body--no-scroll-y');
                    labelsInViewport.forEach(label => {
                        label.classList.add(classes.active);
                    });
                    let scrollDirection;
                    let scroll = 0;
                    let timeout;
                    const observer = Observer.create({
                        target: window,
                        type: 'wheel,touch,scroll,mousewheel,DOMMouseScroll', // Ajouter les événements "mousewheel" et "DOMMouseScroll"
                        onChange: (self) => {
                          clearTimeout(timeout);
                          if (!scrollDirection) {
                            scrollDirection = self.deltaY || self.wheelDeltaY; // Utiliser self.deltaY ou self.wheelDeltaY en fonction de l'événement déclenché
                          }
                          if (scroll < 0 || scroll > 120) {
                            labelsInViewport.forEach(label => {
                              label.classList.remove(classes.active);
                            });
                            observer.kill();
                            setTimeout(() => {
                              document.body.classList.remove('body--no-scroll-y');
                            }, 500); // add a delay of 0.1 second before removing the class
                        }
                      },
                      onUp: () => {
                        if (scrollDirection < 0) {
                          scroll++;
                        } else {
                          scroll--;
                        }
                      },
                      onDown: () => {
                        if (scrollDirection < 0) {
                          scroll--;
                        } else {
                          scroll++;
                        }
                      }
                    });
                  }             
            }
        }
    });
}

const reset = el => {
    el.animation.scrollTrigger.kill();
}

const init = () => {
    const els = document.querySelectorAll(selectors.el);
    els.forEach(build);
    window.addEventListener('resize', () => {
        els.forEach(reset);
        els.forEach(build);
    });
};

const module = () => {
    init();
    document.addEventListener('domUpdated', init);
};

export default module;